/** @fileoverview Gates Fabrik routes by identity, tenant access, and billing status. */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatPage from '../pages/ChatPage';
import AppBuilderPage from '../pages/AppBuilderPage';
import AppPreviewPage from '../pages/AppPreviewPage';
import WorkspacePicker from './WorkspacePicker';
import { FabrikAccessDenied, FabrikBillingBlocked } from './shell/FabrikAccessStatus';
import FabrikShellHeader from './shell/FabrikShellHeader';
import { getFabrikRoute } from './shell/routes';
import { useFabrikBillingStatus, hasFabrikAccess } from '../hooks/useFabrikBillingStatus';
import { getPlatformHomeURL, login } from '../services/platformAuth';
import type { Tenant, User } from '../types/identity';
import type { FabrikAppDefinition } from '../types/apps';
import type { Conversation } from '../types/chat';

type Props = {
  user?: User;
  error: string;
};

export default function FabrikShell({ user, error }: Props) {
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const [creatingApp, setCreatingApp] = useState(false);
  const [exitRequest, setExitRequest] = useState(0);
  const [historyRequest, setHistoryRequest] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [chatPortalTarget, setChatPortalTarget] = useState<HTMLElement | null>(null);
  const [portalChatOptions, setPortalChatOptions] = useState<{
    previewOpen: boolean;
    onPreviewToggle: () => void;
    onAppDraftUpdated: (definition: FabrikAppDefinition) => void;
  } | null>(null);
  const { tenantId, appId, isNewAppRoute, isPreviewRoute, isAppEditRoute } =
    getFabrikRoute(pathname);
  const initialConversation = (state as { conversation?: Conversation } | null)?.conversation;
  const allTenants: Tenant[] = user?.tenants || [];
  const fabrikTenants = allTenants.filter(hasFabrikAccess);
  const tenant = allTenants.find((item) => item.tenant_id === tenantId);
  const tenantHasFabrikAccess = hasFabrikAccess(tenant);
  const billingStatus = useFabrikBillingStatus(tenant, tenantHasFabrikAccess);
  const platformHomeURL = getPlatformHomeURL(tenantId);
  const fabrikHomeURL = tenantId ? `/t/${encodeURIComponent(tenantId)}` : '/';

  const chooseTenant = (id: string) => {
    setCreatingApp(false);
    setExitRequest((request) => request + 1);
    navigate(`/t/${encodeURIComponent(id)}`);
  };
  const goToFabrikHome = () => {
    setCreatingApp(false);
    setExitRequest((request) => request + 1);
    navigate(fabrikHomeURL);
  };

  const autoTenant =
    allTenants.length === 1
      ? allTenants[0]
      : fabrikTenants.length === 1
      ? fabrikTenants[0]
      : undefined;

  useEffect(() => {
    if (!autoTenant || tenantId) return;
    navigate(`/t/${encodeURIComponent(autoTenant.tenant_id)}`, { replace: true });
  }, [autoTenant, navigate, tenantId]);

  return (
    <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-app-surface-card font-sans text-app-text">
      {!isPreviewRoute && !isAppEditRoute && (
        <FabrikShellHeader
          user={user}
          tenants={allTenants}
          selectedTenantId={tenant?.tenant_id || ''}
          tenant={tenantHasFabrikAccess ? tenant : undefined}
          platformHomeURL={platformHomeURL}
          creatingApp={creatingApp}
          isNewAppRoute={isNewAppRoute}
          onSelectTenant={chooseTenant}
          onGoToFabrikHome={goToFabrikHome}
          isHistoryOpen={historyOpen}
          onToggleHistory={() => setHistoryRequest((request) => request + 1)}
        />
      )}
      {tenant && !tenantHasFabrikAccess ? (
        <FabrikAccessDenied />
      ) : tenant && billingStatus !== 'allowed' ? (
        <FabrikBillingBlocked
          checking={billingStatus === 'loading' || billingStatus === 'idle'}
          failed={billingStatus === 'error'}
        />
      ) : tenant ? (
        <>
          {!isPreviewRoute &&
            !(isNewAppRoute && new URLSearchParams(search).get('mode') === 'canvas') && (
              <ChatPage
                key={tenant.tenant_id}
                tenant={tenant}
                user={user}
                appId={isNewAppRoute ? undefined : appId}
                initialConversation={isAppEditRoute ? initialConversation : undefined}
                exitRequest={exitRequest}
                historyRequest={historyRequest}
                onHistoryViewChange={setHistoryOpen}
                onCreateModeChange={setCreatingApp}
                initialView={isNewAppRoute || isAppEditRoute ? 'interview' : 'home'}
                portalTarget={isNewAppRoute || isAppEditRoute ? chatPortalTarget : null}
                portalPreviewOpen={portalChatOptions?.previewOpen}
                onPortalDraftUpdated={portalChatOptions?.onAppDraftUpdated}
              />
            )}
          {isPreviewRoute ? (
            <AppPreviewPage tenantId={tenant.tenant_id} appId={appId} tenant={tenant} user={user} />
          ) : isNewAppRoute ? (
            <AppBuilderPage
              key={`${tenant.tenant_id}:new`}
              tenant={tenant}
              user={user}
              setChatPortalTarget={setChatPortalTarget}
              setPortalChatOptions={setPortalChatOptions}
            />
          ) : isAppEditRoute ? (
            <AppBuilderPage
              key={`${tenant.tenant_id}:${appId}`}
              tenant={tenant}
              user={user}
              appId={appId}
              setChatPortalTarget={setChatPortalTarget}
              setPortalChatOptions={setPortalChatOptions}
            />
          ) : null}
        </>
      ) : (
        <WorkspacePicker
          user={user}
          tenants={fabrikTenants}
          tenantId={tenantId}
          error={error}
          autoSelecting={Boolean(autoTenant && !tenantId)}
          onSelect={chooseTenant}
          onLogin={login}
        />
      )}
    </div>
  );
}
